import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type I18nSurface = 'admin_ui' | 'pos' | 'sco' | 'attendant' | 'shared' | 'receipt_label';

export const I18N_SURFACES: I18nSurface[] = [
  'admin_ui',
  'pos',
  'sco',
  'attendant',
  'shared',
  'receipt_label',
];

export const SURFACE_LABEL: Record<I18nSurface, string> = {
  admin_ui: 'Admin UI',
  pos: 'POS',
  sco: 'SCO',
  attendant: 'Attendant',
  shared: 'Shared',
  receipt_label: 'Receipt labels',
};

/**
 * Catalogue keys for the surface names, resolved as
 * `t(SURFACE_LABEL_KEY[s], SURFACE_LABEL[s])`.
 *
 * `Admin UI`, `POS` and `SCO` have rows too even though every locale keeps the
 * same text: a surface that cannot be translated today may want to be later, and
 * a half-declared enum is how a set drifts.
 */
export const SURFACE_LABEL_KEY: Record<I18nSurface, string> = {
  admin_ui: 'i18nEditor.surface.adminUi',
  pos: 'i18nEditor.surface.pos',
  sco: 'i18nEditor.surface.sco',
  attendant: 'i18nEditor.surface.attendant',
  shared: 'i18nEditor.surface.shared',
  receipt_label: 'i18nEditor.surface.receiptLabel',
};

async function fetchStrings(surface: I18nSurface, locale: string): Promise<Record<string, string>> {
  const res = await apiClient.get<Record<string, string>>('/i18n/strings', {
    params: { surface, locale },
  });
  return res.data;
}

export function useI18nStrings(surface: I18nSurface, locale: string) {
  return useQuery({
    queryKey: ['i18n', 'strings', surface, locale],
    queryFn: () => fetchStrings(surface, locale),
    enabled: locale !== '',
  });
}

export function useI18nStringsBulk(surface: I18nSurface, locales: string[]) {
  return useQueries({
    queries: locales.map((locale) => ({
      queryKey: ['i18n', 'strings', surface, locale] as const,
      queryFn: () => fetchStrings(surface, locale),
      enabled: locale !== '',
    })),
  });
}

export function useUpsertI18nString() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      surface: I18nSurface;
      localeCode: string;
      key: string;
      value: string;
    }) => {
      await apiClient.put<{ ok: true }>('/i18n/strings', vars);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['i18n', 'strings', vars.surface, vars.localeCode] });
    },
  });
}
